import { Calculator, TrendingUp, BarChart3, Activity } from "lucide-react";

export const KpisCalculationMethods = () => {
    const methods = [
        {
            title: "Simple Weights",
            icon: <Calculator className="w-5 h-5" />,
            bgColor: "var(--color-general-green)",
            description: "Direct multiplication of events by predefined weights",
            formula: "Points = Min(event × weight, maxValue)",
            example: (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4" style={{ color: "var(--color-general-green)" }} />
                        <span className="text-xs">Player scores 2 goals</span>
                    </div>
                    <div
                        className="rounded-lg p-2 border"
                        style={{
                            backgroundColor: "rgba(74, 222, 128, 0.1)",
                            borderColor: "rgba(74, 222, 128, 0.3)",
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-mono">2 × 3 = 6</span>
                            <span className="text-xs" style={{ color: "var(--color-general-green)" }}>
                                Max: 6
                            </span>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Z-Score",
            icon: <TrendingUp className="w-5 h-5" />,
            bgColor: "var(--color-general-blue)",
            description: "Standardized score comparing player performance to match average",
            formula: (
                <div className="space-y-1">
                    <div>Z-Score = (playerValue - matchAverage) / matchStandardDeviation</div>
                    <div>Points = MIN(basePoints + Z-Score × weight, maxValue)</div>
                </div>
            ),
            example: (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" style={{ color: "var(--color-general-blue)" }} />
                        <span className="text-xs font-semibold">CORSI FOR % Example</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-surface-container rounded-md p-2 space-y-1">
                            <p className="text-xs text-on-surface-variant">Match Stats</p>
                            <p className="text-xs">Average: 0</p>
                            <p className="text-xs">Std Dev: 0.1</p>
                        </div>
                        <div
                            className="rounded-lg p-2 space-y-1 border"
                            style={{
                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                borderColor: "rgba(59, 130, 246, 0.3)",
                            }}
                        >
                            <p className="text-xs" style={{ color: "var(--color-general-blue)" }}>
                                Player A
                            </p>
                            <p className="text-xs">CF% Rel: 0.2</p>
                            <p className="text-xs font-bold" style={{ color: "var(--color-general-blue)" }}>
                                Z-Score: 2.0
                            </p>
                        </div>
                    </div>
                    <div className="bg-surface-container rounded-md p-2">
                        <p className="text-xs text-on-surface">
                            Points = MIN(3 + 2.0 × 1.5, 6) =
                            <span className="text-sm font-bold ml-1" style={{ color: "var(--color-general-blue)" }}>
                                6
                            </span>
                        </p>
                    </div>
                </div>
            ),
        },
        {
            title: "Weighted Z-Score",
            icon: <BarChart3 className="w-5 h-5" />,
            bgColor: "var(--color-general-purple)",
            description: "Z-Score adjusted for volume to prevent small sample bias",
            formula: (
                <div className="space-y-1">
                    <div>adjustedZ-Score = Z-Score × volumeAdjustment</div>
                    <div>Points = Min(basePoints + adjustedZ-Score × weight, maxValue)</div>
                </div>
            ),
            example: (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4" style={{ color: "var(--color-general-purple)" }} />
                        <span className="text-xs font-semibold">Shot Accuracy Comparison</span>
                    </div>
                    <div className="space-y-2">
                        <div
                            className="rounded-lg p-2 border"
                            style={{
                                backgroundColor: "rgba(168, 85, 247, 0.1)",
                                borderColor: "rgba(168, 85, 247, 0.3)",
                            }}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs">Player A (10 shots)</span>
                                <span className="text-sm font-bold" style={{ color: "var(--color-general-purple)" }}>
                                    90%
                                </span>
                            </div>
                        </div>
                        <div className="bg-surface-container rounded-md p-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs">Player B (1 shot)</span>
                                <span className="text-sm text-on-surface-variant">100%</span>
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-on-surface-variant italic">Volume adjustment ensures fair comparison</p>
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-4">
            <h3 className="text-base font-headline font-bold text-on-surface">Calculation Methods</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {methods.map((method, index) => (
                    <div
                        key={method.title}
                        className="glass card card-slide-up rounded-xl p-4"
                        style={{ animationDelay: `${0.3 + index * 0.05}s` }}
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 rounded-lg text-white" style={{ backgroundColor: method.bgColor }}>
                                {method.icon}
                            </div>
                            <h3 className="text-base font-semibold text-on-surface">{method.title}</h3>
                        </div>
                        <p className="text-sm text-on-surface mb-3">{method.description}</p>

                        <div className="bg-surface-container-lowest rounded-md p-3 border border-outline-variant/20 mb-3">
                            <p className="text-xs text-on-surface-variant mb-2">Formula</p>
                            <div className="text-xs font-mono text-on-surface">{method.formula}</div>
                        </div>

                        <div className="bg-surface-container-lowest rounded-md p-3 border border-outline-variant/20">
                            <p className="text-xs text-on-surface-variant mb-2">Example</p>
                            <div className="text-on-surface">{method.example}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
